// src/belediye/components/reports/filter/ReportFilterBasic.js
//
// YENİ YAPI:
// ─ Normal mod:   2 kolon (Tarih | Başvuru)
// ─ Gelişmiş mod: 3 kolon — 3. kolon inline olarak açılır, aşağı hiçbir şey açılmaz
// ─ 3. kolon içeriği: 4 dataset toggle (alt alta) + ayırıcı + tek personel select
// ─ Personel: TÜM alanlar için tek seçici (reviewed_by + resolved_by + forward_by
//   + close_request_requested_by + close_request_decided_by + close_request_applied_by)
// ─ Kapatma Talebi Durumu KALDIRILDl
// ─ Tüm input beyaz (#fff)
// ─ Dropdown: position:fixed portal
// ─ ReportFilterAdvanced artık bu dosyayla birleştirildi → ayrı çağrıya gerek yok

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  refreshReportV1Options,
  setDraftFilter,
  setDraftFiltersBulk,
  setOpenDropdown,
  selectReportV1Config,
  selectReportV1Options,
  selectReportV1DraftFilters,
  selectReportV1IsPresidentLike,
  selectReportV1AdvancedOpen,
  selectReportV1OpenDropdown,
} from "../../../redux/slices/belediyeReportV1Slice";

import {
  CalendarDaysIcon,
  IdentificationIcon,
  BuildingOffice2Icon,
  HomeModernIcon,
  TagIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  CheckIcon,
  ArrowsRightLeftIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  UserCircleIcon,
  XMarkIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

import "./ReportFilter.css";

/* =============================================================================
   Helpers
============================================================================= */

const ALL_VALUE = "all";

const toInt = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const ensureArray = (v) => {
  if (v === null || v === undefined) return [];
  return Array.isArray(v) ? v : [v];
};

const uniq = (arr) => {
  const s = new Set();
  const out = [];
  ensureArray(arr).forEach((x) => {
    const key = x === null || x === undefined ? "" : String(x);
    if (!key || s.has(key)) return;
    s.add(key);
    out.push(x);
  });
  return out;
};

const uniqStrings = (arr) => {
  const s = new Set();
  const out = [];
  ensureArray(arr).forEach((x) => {
    const k = x === null || x === undefined ? "" : String(x).trim();
    if (!k || s.has(k)) return;
    s.add(k);
    out.push(k);
  });
  return out;
};

const stableArrKey = (arr) => ensureArray(arr).map(String).sort().join("|");

const formatYmd = (d) => {
  const yyyy = String(d.getFullYear());
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const subtractDays = (days) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - Number(days));
  return d;
};

/* =============================================================================
   Dataset tanımları
============================================================================= */

const DATASET_DEFS = [
  { key: "status_logs",           label: "Durum Logları",          Icon: ClipboardDocumentListIcon },
  { key: "forward_logs",          label: "Yönlendirme Logları",    Icon: ArrowsRightLeftIcon       },
  { key: "operation_logs",        label: "İşlem Logları",          Icon: WrenchScrewdriverIcon     },
  { key: "close_request_history", label: "Kapatma Talep Geçmişi",  Icon: DocumentCheckIcon         },
];

/* =============================================================================
   PopoverSelect — position:fixed dropdown
   Tek component, hem single hem multi destekler.
============================================================================= */

function PopoverSelect({
  dropdownKey,
  openDropdown,
  setOpenDropdownKey,
  label,
  icon: Icon,
  placeholder,
  multiple      = false,
  searchable    = false,
  options       = [],
  selectedValue,    // single
  selectedValues,   // multi
  onChange,
  onSearch,
  searchValue,
  disabled      = false,
  clearable     = false,
}) {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef   = useRef(null);
  const isOpen     = openDropdown === dropdownKey;

  /* ── Fixed konumlama ── */
  const positionPanel = () => {
    const t = triggerRef.current;
    const p = panelRef.current;
    if (!t || !p) return;

    const rect  = t.getBoundingClientRect();
    const vpH   = window.innerHeight;
    const pH    = p.offsetHeight || 300;
    const openUp = vpH - rect.bottom - 8 < pH && rect.top > vpH - rect.bottom;

    p.style.left  = `${rect.left}px`;
    p.style.width = `${Math.max(rect.width, 200)}px`;

    if (openUp) {
      p.style.top    = "";
      p.style.bottom = `${vpH - rect.top}px`;
    } else {
      p.style.top    = `${rect.bottom + 3}px`;
      p.style.bottom = "";
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    window.addEventListener("resize", positionPanel);

    const onDown = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target))   return;
      setOpenDropdownKey(null);
    };
    document.addEventListener("mousedown", onDown);

    return () => {
      window.removeEventListener("scroll", positionPanel, true);
      window.removeEventListener("resize", positionPanel);
      document.removeEventListener("mousedown", onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /* ── Seçili set (multi) ── */
  const selectedSet = useMemo(
    () => new Set(ensureArray(selectedValues).map((x) => String(x))),
    [selectedValues]
  );

  /* ── Tümü seçili mi (multi) ── */
  const allOpt = useMemo(
    () => (options || []).find((o) => o?.meta?.all),
    [options]
  );

  const isAllSelected = useMemo(() => {
    if (!multiple || !allOpt) return false;
    return selectedSet.size === 0 || selectedSet.has(String(allOpt.value));
  }, [multiple, allOpt, selectedSet]);

  /* ── Özet metin ── */
  const summaryText = useMemo(() => {
    if (!options?.length) return placeholder || "Seçiniz";

    if (multiple) {
      if (isAllSelected && allOpt) return allOpt.label || "Tümü";
      const picked = options.filter(
        (o) => !o?.meta?.all && selectedSet.has(String(o.value))
      );
      if (!picked.length) return placeholder || "Seçiniz";
      if (picked.length <= 2) return picked.map((o) => o.label).join(", ");
      return `${picked[0].label} +${picked.length - 1}`;
    }

    if (selectedValue === null || selectedValue === undefined || selectedValue === "")
      return placeholder || "Seçiniz";
    const hit = options.find((o) => String(o.value) === String(selectedValue));
    return hit?.label || placeholder || "Seçiniz";
  }, [options, placeholder, multiple, isAllSelected, allOpt, selectedSet, selectedValue]);

  const hasValue = multiple
    ? selectedSet.size > 0 && !isAllSelected
    : selectedValue !== null && selectedValue !== undefined && selectedValue !== "";

  /* ── Seçim işlemi ── */
  const handlePick = (opt) => {
    if (disabled) return;
    const val   = String(opt.value);
    const isAll = !!opt?.meta?.all;

    if (!multiple) {
      const same = String(selectedValue) === val;
      onChange?.(same ? null : opt.value);
      setOpenDropdownKey(null);
      return;
    }

    if (isAll) {
      onChange?.([]);
      return;
    }

    if (isAllSelected) { onChange?.([val]); return; }

    const next = new Set(selectedSet);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange?.(Array.from(next));
  };

  return (
    <div className="rfb-popover" ref={wrapperRef}>
      {/* Label */}
      {label && (
        <div className="rfb-field-label" style={{ marginBottom: 4 }}>
          <span className="rfb-field-label__row">
            {Icon && <Icon className="rfb-field-label__icon" />}
            <span>{label}</span>
            {clearable && hasValue && !disabled && (
              <button
                type="button"
                title="Temizle"
                style={{
                  border: "none", background: "transparent",
                  padding: 0, cursor: "pointer",
                  display: "inline-flex", marginLeft: 2,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(multiple ? [] : null);
                }}
              >
                <XMarkIcon style={{ width: 11, height: 11, color: "var(--color-text-muted)" }} />
              </button>
            )}
          </span>
        </div>
      )}

      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        className={`rfb-dd-trigger${disabled ? " rfb-dd-trigger--disabled" : ""}`}
        onClick={() => !disabled && setOpenDropdownKey(isOpen ? null : dropdownKey)}
        aria-expanded={isOpen}
        disabled={disabled}
      >
        <span className="rfb-dd-trigger__text">{summaryText}</span>
        <ChevronDownIcon className="rfb-dd-trigger__chevIcon" />
      </button>

      {/* Panel */}
      {isOpen && !disabled && (
        <div ref={panelRef} className="rfb-dd-panel" style={{ position: "fixed" }}>
          {searchable && (
            <div className="rfb-dd-search">
              <MagnifyingGlassIcon className="rfb-dd-search__icon" />
              <input
                className="rfb-dd-search__input"
                value={searchValue || ""}
                placeholder="Ara..."
                autoFocus
                onChange={(e) => onSearch?.(e.target.value)}
              />
            </div>
          )}

          <div className="rfb-dd-list">
            {(options || []).map((opt) => {
              const isAll   = !!opt?.meta?.all;
              const checked = multiple
                ? (isAll ? isAllSelected : selectedSet.has(String(opt.value)))
                : String(opt.value) === String(selectedValue);

              return (
                <button
                  key={String(opt.key ?? opt.value)}
                  type="button"
                  className={`rfb-dd-item${checked ? " is-selected" : ""}${isAll ? " is-all" : ""}`}
                  onClick={() => handlePick(opt)}
                >
                  <span className={`rfb-dd-check${checked ? " on" : ""}`}>
                    {checked && <CheckIcon className="rfb-dd-check__icon" />}
                  </span>
                  <span className="rfb-dd-item__label">{opt.label}</span>
                </button>
              );
            })}
            {!options?.length && <div className="rfb-dd-empty">Seçenek yok</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   ReportFilterBasic  (+ inline Advanced 3. kolon)
============================================================================= */

export default function ReportFilterBasic() {
  const dispatch = useDispatch();

  const configState     = useSelector(selectReportV1Config);
  const optionsState    = useSelector(selectReportV1Options);
  const draft           = useSelector(selectReportV1DraftFilters);
  const isPresidentLike = useSelector(selectReportV1IsPresidentLike);
  const advancedOpen    = useSelector(selectReportV1AdvancedOpen);
  const openDropdown    = useSelector(selectReportV1OpenDropdown);

  const configData  = configState?.data || null;
  const optionsData = optionsState?.data || null;

  /* ── options raw ── */
  const unitOptionsRaw         = optionsData?.units?.options         || [];
  const neighborhoodOptionsRaw = optionsData?.neighborhoods?.options || [];
  const statusOptionsRaw       = optionsData?.statuses?.options || optionsData?.status?.options || [];
  const staffUserOptionsRaw    = optionsData?.staff_users?.options   || [];
  const staffUsersDisabled     = !!optionsData?.staff_users?.meta?.disabled;

  const activeUnitName = configData?.active_unit?.name || configData?.active_unit?.slug || "";

  const staffUnitLabel = useMemo(() => {
    if (activeUnitName) return activeUnitName;
    return (unitOptionsRaw || []).find((o) => !o?.meta?.all)?.label || "";
  }, [activeUnitName, unitOptionsRaw]);

  /* ── Normalize options ── */
  const unitOptions = useMemo(
    () => (unitOptionsRaw || []).map((o) => ({
      key: String(o.key), label: String(o.label),
      value: o?.meta?.all ? ALL_VALUE : String(o?.value), meta: o.meta || {},
    })),
    [unitOptionsRaw]
  );

  const neighborhoodOptions = useMemo(
    () => (neighborhoodOptionsRaw || []).map((o) => ({
      key: String(o.key), label: String(o.label),
      value: o?.meta?.all ? ALL_VALUE : String(o?.value), meta: o.meta || {},
    })),
    [neighborhoodOptionsRaw]
  );

  const statusOptions = useMemo(
    () => (statusOptionsRaw || [])
      .filter((o) => o?.meta?.all || String(o?.value) !== "yönlendirildi")
      .map((o) => ({
        key: String(o.key), label: String(o.label),
        value: o?.meta?.all ? ALL_VALUE : String(o?.value), meta: o.meta || {},
      })),
    [statusOptionsRaw]
  );

  const staffUserOptions = useMemo(
    () => (staffUserOptionsRaw || []).map((o) => ({
      key:   String(o.key),
      label: String(o.label),
      value: o?.value?.id ?? o?.value ?? o.key,
      meta:  o.meta || {},
    })),
    [staffUserOptionsRaw]
  );

  /* ── Search states ── */
  const [qUnits, setQUnits]               = useState("");
  const [qNeighborhoods, setQNeighborhoods] = useState("");
  const [qUsers, setQUsers]               = useState("");
  const debounceRef = useRef({ t: null });

  useEffect(() => () => { if (debounceRef.current.t) clearTimeout(debounceRef.current.t); }, []);

  const setOpenDropdownKey = (k) => dispatch(setOpenDropdown(k));

  const scheduleRefresh = (params) => {
    if (debounceRef.current.t) clearTimeout(debounceRef.current.t);
    debounceRef.current.t = setTimeout(() => dispatch(refreshReportV1Options(params || {})), 250);
  };

  /* ── Draft: basic ── */
  const draftUnitsMode = draft?.units_mode || "all";

  const draftUnitIds = useMemo(
    () => uniq(ensureArray(draft?.units).map(toInt).filter((n) => n !== null)),
    [draft?.units]
  );

  const unitAllSelected = isPresidentLike && (draftUnitsMode === "all" || draftUnitIds.length === 0);

  const selectedUnitValuesForUI = useMemo(() => {
    if (!isPresidentLike || unitAllSelected) return [];
    return draftUnitIds.map(String);
  }, [draftUnitIds, isPresidentLike, unitAllSelected]);

  const draftNeighborhoodIds = useMemo(
    () => uniq(ensureArray(draft?.neighborhoods).map(toInt).filter((n) => n !== null)),
    [draft?.neighborhoods]
  );

  const draftStatuses = useMemo(
    () => uniq(
      ensureArray(draft?.statuses)
        .map((x) => String(x ?? "").trim())
        .filter((s) => s && s !== "yönlendirildi")
    ),
    [draft?.statuses]
  );

  /* ── Draft: advanced datasets ── */
  const draftDatasets = useMemo(
    () => uniqStrings(draft?.advanced_datasets),
    [draft?.advanced_datasets]
  );

  const datasetsAllowed = useMemo(
    () => ensureArray(optionsData?.datasets_allowed).map(String),
    [optionsData]
  );

  const datasetEnabled = (key) =>
    !datasetsAllowed.length || datasetsAllowed.includes(String(key));

  const toggleDataset = (key) => {
    if (!datasetEnabled(key)) return;
    const cur = new Set(draftDatasets);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    dispatch(setDraftFiltersBulk({ advanced_datasets: Array.from(cur), page: 1 }));
  };

  /* ── Draft: TEK personel alanı ──
     Seçilen kişi → tüm personel alanlarına aynı anda yazılır.
     Backend, hangi alan için kullandığını dataset'e göre belirler.
  ── */
  const selectedPersonelIds = useMemo(() => {
    const raw = ensureArray(draft?.staff_user_ids);
    return uniq(raw.map(toInt).filter((n) => n !== null)).map(String);
  }, [draft?.staff_user_ids]);

  const setPersonel = (vals) => {
    const ids = uniq(ensureArray(vals).map(toInt).filter((n) => n !== null));
    dispatch(setDraftFiltersBulk({
      staff_user_ids: ids,
      // eski tekil alanlar temizlenir — anlam artık staff_user_ids OR kümesinde
      reviewed_by:                  null,
      resolved_by:                  null,
      forward_by:                   null,
      forwarded_by:                 null,
      close_request_requested_by:   null,
      close_request_decided_by:     null,
      close_request_applied_by:     null,
      page: 1,
    }));
  };

  /* ── Setters ── */
  const onPresetDays = (days) => {
    dispatch(setDraftFiltersBulk({
      date_from: formatYmd(subtractDays(days)),
      date_to:   formatYmd(new Date()),
      page: 1,
    }));
  };

  const setUnitsFromUI = (vals) => {
    const arr = ensureArray(vals).map(String);
    if (!arr.length) {
      // Tümü seçili — her iki alias'ı da temizle
      dispatch(setDraftFiltersBulk({ units: [], unit_ids: [], units_mode: "all", page: 1 }));
      return;
    }
    const ids = uniq(arr.map(toInt).filter((n) => n !== null));
    const mode = ids.length ? "selected" : "all";
    // units VE unit_ids her ikisi de set ediliyor — slice applyRoleAndUiRules her ikisine de bakıyor
    dispatch(setDraftFiltersBulk({ units: ids, unit_ids: ids, units_mode: mode, page: 1 }));
  };

  const setNeighborhoodFromUI = (vals) => {
    const arr = ensureArray(vals).map(String);
    if (!arr.length) {
      dispatch(setDraftFiltersBulk({ neighborhoods: [], neighborhood: [], page: 1 }));
      return;
    }
    const ids = uniq(arr.map(toInt).filter((n) => n !== null));
    // neighborhoods VE neighborhood alias'ı birlikte set ediliyor
    dispatch(setDraftFiltersBulk({ neighborhoods: ids, neighborhood: ids, page: 1 }));
  };

  const setStatusFromUI = (vals) => {
    const arr = uniq(ensureArray(vals).map((x) => String(x ?? "").trim()).filter(Boolean));
    const cleaned = arr.filter((s) => s !== "yönlendirildi");
    // statuses VE status alias'ı birlikte set ediliyor — slice normalize ederken her ikisine bakıyor
    dispatch(setDraftFiltersBulk({ statuses: cleaned, status: cleaned, page: 1 }));
  };

  /* Birim değişince mahalle seçeneklerini yenile — sadece kullanıcı birim seçince.
     Mount anında çalışmaz (skipFirstRef). Query tetiklemez — sadece options. */
  const skipUnitRefreshRef = useRef(true);
  useEffect(() => {
    if (skipUnitRefreshRef.current) {
      skipUnitRefreshRef.current = false;
      return;
    }
    if (!isPresidentLike) return;
    dispatch(refreshReportV1Options({
      units: unitAllSelected || !draftUnitIds.length ? undefined : draftUnitIds,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPresidentLike, unitAllSelected, stableArrKey(draftUnitIds), dispatch]);

  const dateModeDisabled = !isPresidentLike;

  /* ── Tarih baypas durumu (görsel ipucu) ──
     Takip No / Başvuru ID / Arama / Personel doluysa sorgu tarihten bağımsızdır. */
  const hasTracking      = !!String(draft?.tracking_number || "").trim();
  const hasAppId         = !!String(draft?.application_id || "").trim();
  const hasSearch        = !!String(draft?.search || "").trim();
  const personelSelected = selectedPersonelIds.length > 0;
  const dateIgnored      = hasTracking || hasAppId || hasSearch || personelSelected;
  const bypassReason     = [
    (hasTracking || hasAppId || hasSearch) ? "takip no / ID / arama" : null,
    personelSelected ? "personel" : null,
  ].filter(Boolean).join(" + ");

  /* ════════════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════════════ */
  return (
    <div className="report-filterbar">
      <div className={`rfb-grid${advancedOpen && isPresidentLike ? " rfb-grid--with-advanced" : ""}`}>

        {/* ─── KOLON 1: Tarih ──────────────────────────────────── */}
        <div className={`rfb-block${dateIgnored ? " rfb-block--date-ignored" : ""}`}>
          <div className="rfb-block__title">
            <CalendarDaysIcon className="rfb-block__icon" />
            Tarih
          </div>

          {dateIgnored && (
            <div className="rfb-bypass-note">
              Tarih yok sayılıyor ({bypassReason})
            </div>
          )}

          <div className="rfb-date-wrap">
            <div className="rfb-date-range">
              <div className="rfb-field">
                <div className="rfb-field-label">Başlangıç</div>
                <input
                  type="date"
                  className="md-input"
                  value={draft?.date_from || ""}
                  onChange={(e) =>
                    dispatch(setDraftFilter({ key: "date_from", value: e.target.value || null }))
                  }
                />
              </div>
              <div className="rfb-field">
                <div className="rfb-field-label">Bitiş</div>
                <input
                  type="date"
                  className="md-input"
                  value={draft?.date_to || ""}
                  onChange={(e) =>
                    dispatch(setDraftFilter({ key: "date_to", value: e.target.value || null }))
                  }
                />
              </div>
            </div>

            <div className="rfb-field">
              <div className="rfb-field-label">Tarih Modu</div>
              <div className={`rfb-seg${dateModeDisabled ? " is-disabled" : ""}`}>
                <button
                  type="button"
                  className={`rfb-seg__btn${String(draft?.date_mode || "applications") === "applications" ? " is-on" : ""}`}
                  onClick={() => !dateModeDisabled && dispatch(setDraftFilter({ key: "date_mode", value: "applications" }))}
                  disabled={dateModeDisabled}
                >
                  Başvuru
                </button>
                <button
                  type="button"
                  className={`rfb-seg__btn${String(draft?.date_mode || "applications") === "events" ? " is-on" : ""}`}
                  onClick={() => !dateModeDisabled && dispatch(setDraftFilter({ key: "date_mode", value: "events" }))}
                  disabled={dateModeDisabled}
                >
                  Olay
                </button>
              </div>
            </div>

            <div className="rfb-presets">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  type="button"
                  className="btn-md"
                  onClick={() => onPresetDays(d)}
                >
                  Son {d} gün
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ─── KOLON 2: Başvuru Filtreleri ─────────────────────── */}
        <div className="rfb-block">
          <div className="rfb-block__title">
            <IdentificationIcon className="rfb-block__icon" />
            Başvuru Filtreleri
          </div>

          <div className="rfb-app-wrap">
            {/* Sol: dropdown'lar */}
            <div className="rfb-app-col">
              {isPresidentLike ? (
                <PopoverSelect
                  dropdownKey="units"
                  openDropdown={openDropdown}
                  setOpenDropdownKey={setOpenDropdownKey}
                  label="Birim"
                  icon={BuildingOffice2Icon}
                  placeholder="Tüm birimler"
                  multiple={!!optionsData?.units?.multiple}
                  searchable
                  options={unitOptions}
                  selectedValues={selectedUnitValuesForUI}
                  onChange={setUnitsFromUI}
                  onSearch={(v) => { setQUnits(v); scheduleRefresh({ q_units: v || undefined }); }}
                  searchValue={qUnits}
                />
              ) : (
                <div className="rfb-field">
                  <div className="rfb-field-label">
                    <span className="rfb-field-label__row">
                      <BuildingOffice2Icon className="rfb-field-label__icon" />
                      <span>Birim</span>
                    </span>
                  </div>
                  <div className="rfb-readonly">{staffUnitLabel || "—"}</div>
                </div>
              )}

              <PopoverSelect
                dropdownKey="neighborhoods"
                openDropdown={openDropdown}
                setOpenDropdownKey={setOpenDropdownKey}
                label="Mahalle"
                icon={HomeModernIcon}
                placeholder="Tüm mahalleler"
                multiple
                searchable
                options={neighborhoodOptions}
                selectedValues={draftNeighborhoodIds.map(String)}
                onChange={setNeighborhoodFromUI}
                onSearch={(v) => { setQNeighborhoods(v); scheduleRefresh({ q_neighborhoods: v || undefined }); }}
                searchValue={qNeighborhoods}
              />

              <PopoverSelect
                dropdownKey="statuses"
                openDropdown={openDropdown}
                setOpenDropdownKey={setOpenDropdownKey}
                label="Durum"
                icon={TagIcon}
                placeholder="Tüm durumlar"
                multiple
                options={statusOptions}
                selectedValues={draftStatuses}
                onChange={setStatusFromUI}
              />
            </div>

            {/* Sağ: metin girdiler */}
            <div className="rfb-app-col">
              <div className="rfb-field">
                <div className="rfb-field-label">Takip No</div>
                <input
                  className={`md-input${hasTracking ? " is-filled is-bypass" : ""}`}
                  value={draft?.tracking_number || ""}
                  placeholder="Örn: 6DGEROIA"
                  onChange={(e) =>
                    dispatch(setDraftFilter({ key: "tracking_number", value: e.target.value }))
                  }
                />
              </div>

              <div className="rfb-field">
                <div className="rfb-field-label">
                  <span className="rfb-field-label__row">
                    <MagnifyingGlassIcon className="rfb-field-label__icon" />
                    <span>Arama</span>
                  </span>
                </div>
                <input
                  className={`md-input${hasSearch ? " is-filled is-bypass" : ""}`}
                  value={draft?.search || ""}
                  placeholder="Adres, mahalle, konu..."
                  onChange={(e) =>
                    dispatch(setDraftFilter({ key: "search", value: e.target.value }))
                  }
                />
              </div>

              <div className="rfb-field">
                <div className="rfb-field-label">Başvuru ID</div>
                <input
                  className={`md-input${hasAppId ? " is-filled is-bypass" : ""}`}
                  inputMode="numeric"
                  value={draft?.application_id || ""}
                  placeholder="Örn: 155"
                  onChange={(e) =>
                    dispatch(setDraftFilter({ key: "application_id", value: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* ─── KOLON 3: Gelişmiş (sadece başkanlık + advancedOpen) ─ */}
        {isPresidentLike && advancedOpen && (
          <div className="rfb-block rfb-block--advanced">
            <div className="rfb-block__title">
              <Squares2X2Icon className="rfb-block__icon" />
              Gelişmiş
            </div>

            {/* Dataset toggle'lar */}
            <div className="rfb-dataset-list">
              {DATASET_DEFS.map(({ key, label, Icon }) => {
                const on      = draftDatasets.includes(key);
                const enabled = datasetEnabled(key);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`rfb-flag-pill${on ? " is-on" : ""}${!enabled ? " is-disabled" : ""}`}
                    onClick={() => toggleDataset(key)}
                    disabled={!enabled}
                    title={!enabled ? "Bu dataset yetkinizde kapalı" : label}
                  >
                    <span className="rfb-flag-pill__left">
                      <Icon className="rfb-flag-pill__icon" />
                      <span className="rfb-flag-pill__text">{label}</span>
                    </span>
                    <span className={`rfb-flag-pill__right${on ? " is-on" : ""}`}>
                      {on && <CheckIcon className="rfb-flag-pill__check" />}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Ayırıcı */}
            <div className="rfb-adv-sep" />

            {/* TEK personel seçici */}
            <div className="rfb-adv-staff-label">Personel</div>
            <PopoverSelect
              dropdownKey="adv_personel"
              openDropdown={openDropdown}
              setOpenDropdownKey={setOpenDropdownKey}
              placeholder="Personel seç"
              icon={UserCircleIcon}
              multiple
              searchable
              options={staffUserOptions}
              selectedValues={selectedPersonelIds}
              onChange={setPersonel}
              onSearch={(v) => { setQUsers(v); scheduleRefresh({ q_users: v || undefined }); }}
              searchValue={qUsers}
              disabled={staffUsersDisabled}
              clearable
            />
          </div>
        )}

      </div>
    </div>
  );
}